import type { ComponentProps } from "react";
import { tv } from "tailwind-variants";

import { ProgressBackToCareplanButton } from "@/domains/progress/components/progress-back-to-careplan-button";
import { ProgressChartCard } from "@/domains/progress/components/progress-chart-card";
import { ProgressOutcomesCard } from "@/domains/progress/components/progress-outcomes-card";
import { ProgressSideEffectsLog } from "@/domains/progress/components/progress-side-effects-log";
import { ProgressThankYouCard } from "@/domains/progress/components/progress-thank-you-card";
import type { ProgressChart, ProgressResponse } from "@/domains/progress/models/types";
import {
  buildProgressIdealLineLead,
  buildProgressPageIntro,
  buildProgressPageTitle,
  buildUnscoredSymptomsNote,
  PROGRESS_CHARTS_SECTION_LABEL,
  PROGRESS_NEW_PATIENT_FIRST_POINT_EXPLAINER,
  PROGRESS_NO_CHARTS_NOTE,
} from "@/domains/progress/utils/progress-page-copy";
import {
  collectProgressCharts,
  findFirstPointNoteIndex,
  findIdealLineExplainerIndex,
  selectProgressOutcomeItems,
  selectProgressPotentialDiscount,
  selectProgressSideEffectEntries,
  selectProgressThankYouMessage,
} from "@/domains/progress/utils/progress-page-data";
import { Heading } from "@/ui";

const CHARTS_HEADING_ID = "progress-charts-heading";

const pageLayout = tv({
  base: "flex w-full max-w-4xl grow flex-col gap-6 self-center px-4 pt-8 pb-16 sm:gap-8 sm:px-6 sm:pt-16",
});

/** Visible only to a screen reader: without it the cards' `h3` titles jump a level from the `h1`. */
const ChartsSectionHeading = () => {
  return (
    <Heading className="sr-only" id={CHARTS_HEADING_ID} level={2}>
      {PROGRESS_CHARTS_SECTION_LABEL}
    </Heading>
  );
};

/** `chart.key` carries no uniqueness promise across the three sections, so the index disambiguates. */
const chartCardKey = (chart: ProgressChart, index: number) => {
  return `${index}-${chart.key}`;
};

export type ProgressPageContentProps = ComponentProps<"div"> & {
  firstName: string;
  progress: ProgressResponse;
};

export const ProgressPageContent = ({
  className,
  firstName,
  progress,
  ...props
}: ProgressPageContentProps) => {
  const charts = collectProgressCharts(progress);
  const idealLineExplainerIndex = findIdealLineExplainerIndex(charts);
  const idealLineLead = buildProgressIdealLineLead(charts);
  const firstPointNoteIndex = progress.isNewPatient ? findFirstPointNoteIndex(charts) : -1;
  const outcomeItems = selectProgressOutcomeItems(progress.outcomes);
  const sideEffectEntries = selectProgressSideEffectEntries(progress.sideEffects);
  const thankYouMessage = selectProgressThankYouMessage(progress.thankYou);
  const potentialDiscount =
    thankYouMessage === null ? null : selectProgressPotentialDiscount(progress.thankYou);
  const unscoredSymptomsNote = buildUnscoredSymptomsNote(progress.unscoredSymptoms);

  return (
    <div className={pageLayout({ className })} {...props}>
      <header className="flex flex-col gap-3">
        <Heading level={1}>
          {buildProgressPageTitle({ firstName, isNewPatient: progress.isNewPatient })}
        </Heading>

        <p className="text-content-secondary">
          {buildProgressPageIntro({
            intro: progress.intro,
            isNewPatient: progress.isNewPatient,
          })}
        </p>
      </header>

      <section aria-labelledby={CHARTS_HEADING_ID} className="flex flex-col gap-6 sm:gap-8">
        <ChartsSectionHeading />

        {charts.length === 0 ? (
          <p className="text-content-secondary">{PROGRESS_NO_CHARTS_NOTE}</p>
        ) : null}

        {charts.map((chart, index) => {
          return (
            <ProgressChartCard
              chart={chart}
              firstPointNote={
                index === firstPointNoteIndex
                  ? PROGRESS_NEW_PATIENT_FIRST_POINT_EXPLAINER
                  : undefined
              }
              idealLineLead={
                index === idealLineExplainerIndex && idealLineLead !== null
                  ? idealLineLead
                  : undefined
              }
              key={chartCardKey(chart, index)}
            />
          );
        })}

        {unscoredSymptomsNote === null ? null : (
          <p className="text-sm text-content-secondary">{unscoredSymptomsNote}</p>
        )}
      </section>

      {outcomeItems.length > 0 ? <ProgressOutcomesCard items={outcomeItems} /> : null}

      {sideEffectEntries.length > 0 ? <ProgressSideEffectsLog entries={sideEffectEntries} /> : null}

      {thankYouMessage === null ? null : (
        <ProgressThankYouCard message={thankYouMessage} potentialDiscount={potentialDiscount} />
      )}

      <ProgressBackToCareplanButton className="self-start" />
    </div>
  );
};
